/**
 * Honest outage measurement for one participant's call (#2224), kept free of
 * LiveKit and React so it can be tested on its own. `VoicePathMonitor` feeds
 * it one timestamped sample per second; it answers with outage events that
 * carry their real start and end -- not a count rounded to a coarse interval.
 *
 * The rules (Opus DTX is on, so an unmuted silent mic still sends ~1 tiny
 * packet every 400 ms; silence and a stall are told apart by RTP packet
 * counters, never by audio level alone):
 *  - outbound: our mic is published and unmuted, and the packets we send do
 *    not increase for OUTAGE_MIN_MS -- or we are plainly talking (TalkingWindow:
 *    most level readings of that same second above TALKING_LEVEL) while the
 *    byte rate stays at DTX-only level. Starts at the
 *    last sample that was still fine; ends at the first sample that is fine again.
 *  - inbound: per remote speaker whose mic is published and NOT muted, the
 *    packets received for that track do not increase for OUTAGE_MIN_MS (no
 *    stats at all for an expected track counts as zero packets). One event
 *    per listener-speaker pair. A muted or unpublished speaker never opens one.
 *  - mute: our own mute / mic-off periods are reported as kind "mute" so a
 *    reader can tell "could not be heard" from "chose not to be heard".
 *
 * Events keep a stable id for their whole life, so an upload can be re-sent
 * (with a later `ended_at`) and the server upserts instead of duplicating.
 * While our own connection is down nothing is judged: every stat stalls, and
 * blaming the peers would be wrong. Events open at that moment are closed.
 */
import { UNMUTE_GRACE_MS } from "./voicePath";

/** A gap in packets this long is an outage; anything shorter is jitter. */
export const OUTAGE_MIN_MS = 2000;
/**
 * A speaker we have never seen a packet counter for (the subscription is still
 * completing) gets this long before an inbound outage opens; once a counter
 * has been seen, OUTAGE_MIN_MS applies.
 */
export const FIRST_PACKET_GRACE_MS = UNMUTE_GRACE_MS;
/** While talking, an outbound byte rate below this is DTX-only: nothing real is leaving. */
export const DTX_ONLY_BYTES_PER_S = 2000;
/**
 * Mic level (0-1, LiveKit's analyser at -90..-10 dB) that reads as speech
 * rather than a room's noise floor. SILENT_LEVEL (0.002) only says "the mic
 * is not dead" -- far too low to claim someone is talking. The server-side
 * call monitor (services/assistant core/monitor/tracker.py `_speech`) cannot
 * hear levels at all: its "speech" is the byte rate above DTX_ONLY_BYTES_PER_S,
 * i.e. the other half of this same test.
 */
export const TALKING_LEVEL = 0.05;
/** Share of a second's level readings that must be above TALKING_LEVEL to count as talking. */
export const TALKING_SHARE = 0.5;

/**
 * "Are we talking?" judged over the same second as the byte rate it is
 * compared with, not from one instantaneous reading: one loud click inside a
 * quiet second is not speech. `add` each level reading (several a second);
 * `take` answers for the readings since the last `take` and starts over --
 * true when more than TALKING_SHARE of them were above TALKING_LEVEL, null
 * when there were too few to tell (mic muted, analyser not running).
 */
export class TalkingWindow {
  private readings = 0;
  private loud = 0;

  add(level: number | null): void {
    if (level === null) return;
    this.readings += 1;
    if (level > TALKING_LEVEL) this.loud += 1;
  }

  take(): boolean | null {
    const { readings, loud } = this;
    this.readings = this.loud = 0;
    if (readings < 2) return null;
    return loud / readings > TALKING_SHARE;
  }
}

/** A stats read that has not answered by now counts as "no reading" (null). */
export const STATS_READ_TIMEOUT_MS = 2000;

/**
 * `promise`, or `fallback` once `ms` have passed. getStats() can hang on a
 * dying peer connection; a sampler that awaits it unguarded keeps its
 * "already sampling" flag forever and silently stops -- and the not-heard
 * warning with it.
 */
export function settleWithin<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((resolve) => {
    timer = setTimeout(() => resolve(fallback), ms);
  });
  return Promise.race([promise.catch(() => fallback), timeout]).finally(() => clearTimeout(timer));
}

/** Wire cap per upload (the server rejects more). */
export const MAX_OUTAGES_PER_UPLOAD = 500;

/** `not_audible` is produced by AudibilityTracker (lib/audibility.ts) and rides the same upload. */
export type OutageKind = "outbound" | "inbound" | "mute" | "not_audible";

/** The wire shape the call-quality POST expects. Times are epoch ms. */
export interface OutageEvent {
  id: string;
  kind: OutageKind;
  peer_identity: string | null;
  started_at: number;
  ended_at: number | null;
}

export interface LocalSample {
  /** The mic track exists (published). */
  published: boolean;
  muted: boolean;
  /** Cumulative RTP packets sent, or null when the stats read failed. */
  packetsSent: number | null;
  /** Cumulative bytes sent, or null. */
  bytesSent: number | null;
  /** We were talking through this second (TalkingWindow.take), or null when it cannot be told. */
  talking: boolean | null;
}

export interface RemoteSample {
  identity: string;
  /** Their mic is published and not muted: audio is expected from them. */
  expected: boolean;
  /** Cumulative RTP packets received for their mic; null = no stats at all. */
  packetsReceived: number | null;
}

export interface OutageSample {
  /** Epoch ms. */
  at: number;
  /** Our own connection is up. */
  connected: boolean;
  local: LocalSample;
  remotes: RemoteSample[];
}

interface OutboundState {
  lastGoodAt: number;
  prevPackets: number | null;
  prevBytes: number | null;
  prevAt: number;
  openId: string | null;
}

interface InboundState {
  lastGoodAt: number;
  prevPackets: number | null;
  openId: string | null;
}

function defaultNewId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export class OutageTracker {
  private events: OutageEvent[] = [];
  private byId = new Map<string, OutageEvent>();
  private outbound: OutboundState | null = null;
  private inbound = new Map<string, InboundState>();
  private muteId: string | null = null;
  private wasConnected = true;
  private verdict: "ok" | "zero" | "dtx" | "unknown" = "unknown";

  constructor(private readonly newId: () => string = defaultNewId) {}

  observe(sample: OutageSample): void {
    const { at } = sample;
    if (!sample.connected) {
      this.verdict = "unknown";
      // Our own link is down: judge nothing, and end what was open.
      if (this.wasConnected) this.closeAllFlowEvents(at);
      this.wasConnected = false;
      return;
    }
    this.wasConnected = true;
    this.observeLocal(at, sample.local);
    this.observeRemotes(at, sample.remotes);
  }

  /**
   * The participation is ending for good: close every open event at `at`, so
   * the final report never ships an event with no end.
   */
  finish(at: number): void {
    if (this.muteId !== null) this.close(this.muteId, at);
    this.muteId = null;
    this.closeAllFlowEvents(at);
  }

  /** Speakers whose expected audio is not arriving here right now (open inbound outages). */
  missingSpeakers(): string[] {
    const out: string[] = [];
    for (const [identity, s] of this.inbound) if (s.openId) out.push(identity);
    return out;
  }

  /**
   * The latest sample's verdict on our own outgoing audio, before any
   * duration rule: "zero" = no packet left since the previous sample, "dtx" =
   * talking but only DTX-sized traffic left, "ok" = audio is leaving,
   * "unknown" = muted, no stats, first sample, or our link is down. The "not
   * heard" warning applies its own timing to this.
   */
  outboundVerdict(): "ok" | "zero" | "dtx" | "unknown" {
    return this.verdict;
  }

  /**
   * Speakers with an open inbound outage and when it started (the last time
   * their audio was seen arriving -- for a speaker never seen at all, when we
   * started expecting them, so the first-packet grace is already included).
   */
  missingSince(): Map<string, number> {
    const out = new Map<string, number>();
    for (const [identity, s] of this.inbound) {
      if (!s.openId) continue;
      const e = this.byId.get(s.openId);
      if (e) out.set(identity, e.started_at);
    }
    return out;
  }

  /** Our own mic is unmuted but nothing real is leaving (open outbound outage). */
  outboundStalled(): boolean {
    return !!this.outbound?.openId;
  }

  /**
   * Every event since the call began (newest first, capped), as sent on the
   * wire. Open events have `ended_at: null`. Copies, so callers cannot mutate state.
   */
  snapshot(): OutageEvent[] {
    const all = [...this.events].sort((a, b) => b.started_at - a.started_at);
    return all.slice(0, MAX_OUTAGES_PER_UPLOAD).map((e) => ({ ...e }));
  }

  // ── Local: mute periods and outbound stalls ──────────────────────────────

  private observeLocal(at: number, l: LocalSample): void {
    const silenced = !l.published || l.muted;
    this.verdict = "unknown";
    if (silenced) {
      if (this.muteId === null) this.muteId = this.open("mute", null, at);
      // A stall cannot outlive the mute that explains it.
      if (this.outbound?.openId) this.close(this.outbound.openId, at);
      this.outbound = null;
      return;
    }
    if (this.muteId !== null) {
      this.close(this.muteId, at);
      this.muteId = null;
    }

    const prev = this.outbound;
    if (l.packetsSent === null) return; // stats read failed: no verdict, state unchanged
    if (!prev) {
      this.outbound = { lastGoodAt: at, prevPackets: l.packetsSent, prevBytes: l.bytesSent, prevAt: at, openId: null };
      return;
    }

    // Any change counts as alive; a counter that went backwards means the
    // track restarted, which is life, not a stall.
    const packetsUp = prev.prevPackets === null || l.packetsSent !== prev.prevPackets;
    let starved = false;
    if (packetsUp && l.bytesSent !== null && prev.prevBytes !== null && l.talking === true) {
      const dt = Math.max(1, at - prev.prevAt) / 1000;
      const rate = (l.bytesSent - prev.prevBytes) / dt;
      starved = l.bytesSent >= prev.prevBytes && rate < DTX_ONLY_BYTES_PER_S;
    }
    const good = packetsUp && !starved;
    this.verdict = good ? "ok" : packetsUp ? "dtx" : "zero";

    if (good) {
      if (prev.openId) this.close(prev.openId, at);
      prev.openId = null;
      prev.lastGoodAt = at;
    } else if (!prev.openId && at - prev.lastGoodAt >= OUTAGE_MIN_MS) {
      prev.openId = this.open("outbound", null, prev.lastGoodAt);
    }
    prev.prevPackets = l.packetsSent;
    prev.prevBytes = l.bytesSent;
    prev.prevAt = at;
  }

  // ── Remote: one inbound outage per listener-speaker pair ─────────────────

  private observeRemotes(at: number, remotes: RemoteSample[]): void {
    const seen = new Set<string>();
    for (const r of remotes) {
      if (!r.expected) continue;
      seen.add(r.identity);
      const prev = this.inbound.get(r.identity);
      if (!prev) {
        this.inbound.set(r.identity, { lastGoodAt: at, prevPackets: r.packetsReceived, openId: null });
        continue;
      }
      const up =
        r.packetsReceived !== null && (prev.prevPackets === null || r.packetsReceived !== prev.prevPackets);
      if (up) {
        if (prev.openId) this.close(prev.openId, at);
        prev.openId = null;
        prev.lastGoodAt = at;
      } else if (
        !prev.openId &&
        at - prev.lastGoodAt >= (prev.prevPackets === null ? FIRST_PACKET_GRACE_MS : OUTAGE_MIN_MS)
      ) {
        prev.openId = this.open("inbound", r.identity, prev.lastGoodAt);
      }
      if (r.packetsReceived !== null) prev.prevPackets = r.packetsReceived;
    }
    // Muted, unpublished or gone: nothing is owed to us, so end and forget.
    for (const [identity, s] of this.inbound) {
      if (seen.has(identity)) continue;
      if (s.openId) this.close(s.openId, at);
      this.inbound.delete(identity);
    }
  }

  // ── Event bookkeeping ────────────────────────────────────────────────────

  private open(kind: OutageKind, peer: string | null, startedAt: number): string {
    const e: OutageEvent = { id: this.newId(), kind, peer_identity: peer, started_at: startedAt, ended_at: null };
    this.events.push(e);
    this.byId.set(e.id, e);
    return e.id;
  }

  private close(id: string, at: number): void {
    const e = this.byId.get(id);
    if (e && e.ended_at === null) e.ended_at = Math.max(at, e.started_at);
  }

  private closeAllFlowEvents(at: number): void {
    if (this.outbound?.openId) this.close(this.outbound.openId, at);
    this.outbound = null;
    for (const s of this.inbound.values()) if (s.openId) this.close(s.openId, at);
    this.inbound.clear();
    // A mute is a choice, not a flow fault: it carries on across the drop.
  }
}

/**
 * Whether a report should carry `ended: "left"`: the user pressed Leave,
 * closed the tab or reloaded. An app-driven recovery rejoin is a drop, not
 * a leave, so it must not claim one.
 */
export function endedFlag(deliberate: boolean): "left" | undefined {
  return deliberate ? "left" : undefined;
}
